import { useCallback, useEffect, useRef, useState } from 'react';
import { EventBus, EVENTS } from '../../../shared/EventBus';

type Kind = 'buy' | 'learn';
interface Room { sessionId?: string; onMessage(type: string, handler: (data: any) => void): () => void }
interface Loan { room: Room; sid: string; npcId: string; id: string }
export interface NpcRefusal { id: string; npcId: string; message: string }
const REASONS: Record<string, string> = {
  too_far: 'Move closer to continue.', not_enough_gold: 'You need more gold.',
  persistence_failed: 'This could not be saved. Please try again.', error: 'This could not be completed. Please try again.',
  inventory_full: 'There is no room for more of this item.', item_not_available: 'This item is no longer available.',
  already_learned: 'You already know this ability.', ability_not_eligible: 'This ability is not available to you yet.',
  wrong_class: 'This training is for a different calling.', npc_not_found: 'This NPC is no longer available.',
};
const currentRoom = (): Room | null => (globalThis as { nm?: { getRoom?: () => Room | null } }).nm?.getRoom?.() ?? null;

/** One outstanding request per channel/room. Closing the view does not cancel a server transaction. */
export function useNpcTransactions(ownerId: string | undefined) {
  const loans = useRef<Partial<Record<Kind, Loan>>>({});
  const [pending, setPending] = useState<Partial<Record<Kind, Loan>>>({});
  const [refusals, setRefusals] = useState<Partial<Record<Kind, NpcRefusal>>>({});
  const bound = useRef<{ room: Room; sid: string; dispose: Array<() => void> } | null>(null);
  const alive = useRef(false);
  const generation = useRef(0);
  const reset = useCallback(() => { loans.current = {}; if (alive.current) { setPending({}); setRefusals({}); } }, []);
  const unbind = useCallback(() => { generation.current += 1; bound.current?.dispose.forEach(dispose => dispose()); bound.current = null; }, []);
  const settle = useCallback((kind: Kind, payload: any) => {
    if (!payload || typeof payload !== 'object' || typeof payload.ok !== 'boolean') return;
    const loan = loans.current[kind];
    const room = currentRoom();
    if (!loan || loan.room !== room || loan.sid !== room?.sessionId) return;
    // Buy denials have no identity in the existing wire. They can settle only this sole outstanding buy.
    const id = kind === 'learn' ? payload.abilityId : payload.itemId;
    if (id && id !== loan.id) return;
    if (payload.npcId && payload.npcId !== loan.npcId) return;
    if (kind === 'learn' && (!id || !payload.npcId)) return;
    delete loans.current[kind]; setPending({ ...loans.current });
    setRefusals(previous => ({ ...previous, [kind]: payload.ok === true ? undefined : {
      id: loan.id, npcId: loan.npcId, message: REASONS[payload.reason] ?? 'This could not be completed. Please try again.',
    } }));
  }, []);
  const bind = useCallback(() => {
    const room = currentRoom(); const sid = room?.sessionId ?? '';
    if (bound.current?.room === room && bound.current.sid === sid) return;
    unbind(); reset(); if (!room || !sid) return;
    const epoch = generation.current;
    const accept = (kind: Kind, data: unknown) => {
      if (generation.current === epoch && bound.current?.room === room && bound.current.sid === sid && room === currentRoom()) settle(kind, data);
    };
    const dispose = [room.onMessage('buyResult', data => accept('buy', data)),
      room.onMessage('learnAbilityResult', data => accept('learn', data))];
    bound.current = { room, sid, dispose: dispose.filter(value => typeof value === 'function') };
  }, [reset, settle, unbind]);
  useEffect(() => {
    alive.current = true; bind();
    const timer = setInterval(bind, 500);
    const off = EventBus.getInstance().on(EVENTS.NET_DISCONNECTED, () => { unbind(); reset(); });
    return () => { alive.current = false; clearInterval(timer); off(); unbind(); loans.current = {}; };
  }, [ownerId, bind, reset, unbind]);
  const begin = useCallback((kind: Kind, npcId: string, id: string, send: () => void): boolean => {
    bind(); const room = currentRoom();
    if (!room?.sessionId || bound.current?.room !== room || loans.current[kind]) return false;
    const loan: Loan = { room, sid: room.sessionId, npcId, id };
    loans.current[kind] = loan; setPending({ ...loans.current });
    setRefusals(previous => ({ ...previous, [kind]: undefined }));
    try { send(); return true; } catch {
      delete loans.current[kind]; setPending({ ...loans.current });
      setRefusals(previous => ({ ...previous, [kind]: { id, npcId, message: 'You are not connected. Please reconnect and try again.' } }));
      return false;
    }
  }, [bind]);
  return { pending, refusals, begin };
}
